"use client";

// ============================================================
// CLUBHUB – Filterkopf der Rechnungsliste
// ============================================================
// Gebaut aus dem Listen-Baukasten (`components/liste`) wie die
// Rechnungsliste im Agent. Eigen ist dieser Liste:
//   - Status als Zählschiene, „Offen“ ist die Vorgabe.
//   - Art (Beitrag, Rechnung, Gutschrift) und Zeitraum als Wähler.
//   - „Mahnung fällig“ als eigener Schalter — die Frage, mit der
//     die Kasse die Liste meistens aufmacht.
// ============================================================
import { CalendarRange, Layers } from "lucide-react";
import {
  Auswahlfeld,
  Filterkopf,
  Filterzurueck,
  Fuge,
  Luecke,
  Schiene,
  Suchfeld,
  useAnsicht,
  Zaehlknopf,
  Zaehlschiene,
  type Auswahl,
} from "@/components/liste";
import { INVOICE_KIND, INVOICE_STATUS } from "@/lib/labels";
import {
  ALLE_PARAMS,
  OFFEN_STATUS,
  STATUS_REIHE,
  aktiveFilter,
  statusParam,
  umschalten,
  type Ansicht,
} from "@/lib/rechnungen/ansicht";
import type { InvoiceStatus } from "@/generated/prisma/enums";

export function RechnungenFilterkopf({
  ansicht,
  statusZaehler,
  offenZaehler,
  gesamt,
  artZaehler,
  zeiten,
  mahnungFaellig,
}: {
  ansicht: Ansicht;
  statusZaehler: Record<InvoiceStatus, number>;
  offenZaehler: number;
  gesamt: number;
  artZaehler: Record<string, number>;
  zeiten: Auswahl[];
  mahnungFaellig: number;
}) {
  const { setzen, isPending } = useAnsicht();
  const s = ansicht.status;
  const alleAn = s.length === STATUS_REIHE.length;
  const offenAn = s.length === OFFEN_STATUS.length && OFFEN_STATUS.every((x) => s.includes(x));

  return (
    <Filterkopf
      isPending={isPending}
      oben={
        <>
          <Suchfeld wert={ansicht.q} platzhalter="Nummer, Name, Betreff …" beschriftung="Rechnungen durchsuchen" />
          <Schiene>
            <Auswahlfeld
              icon={Layers}
              titel="Art"
              label="Art"
              eintraege={[
                { id: "FEE", name: INVOICE_KIND.FEE, anzahl: artZaehler.FEE ?? 0 },
                { id: "GENERAL", name: INVOICE_KIND.GENERAL, anzahl: artZaehler.GENERAL ?? 0 },
                { id: "CANCELLATION", name: INVOICE_KIND.CANCELLATION, anzahl: artZaehler.CANCELLATION ?? 0 },
              ]}
              gewaehlt={ansicht.art}
              onWaehlen={(id) => setzen({ art: id })}
              suchtext="Art suchen …"
              alleLabel="Alle Arten"
              leerText="Keine Art gefunden"
              breite="schmal"
            />
          </Schiene>
          <Schiene>
            <Auswahlfeld
              icon={CalendarRange}
              titel="Zeitraum"
              label="Zeitraum"
              eintraege={zeiten}
              gewaehlt={ansicht.zeit}
              onWaehlen={(id) => setzen({ zeit: id })}
              suchtext="Jahr oder Quartal …"
              alleLabel="Alle Zeiträume"
              leerText="Kein Zeitraum gefunden"
              breite="schmal"
            />
          </Schiene>
          <Luecke />
          <Zaehlschiene titel="Status">
            <Zaehlknopf
              label="Offen"
              sammel
              anzahl={offenZaehler}
              aktiv={offenAn}
              titel="Offen, teilweise bezahlt und überfällig — die Vorgabe der Liste"
              onClick={() => setzen({ status: null })}
            />
            <Fuge />
            {STATUS_REIHE.map((st) => (
              <Zaehlknopf
                key={st}
                label={INVOICE_STATUS[st].label}
                anzahl={statusZaehler[st] ?? 0}
                aktiv={s.includes(st)}
                ton={st === "OVERDUE" ? "gefahr" : "normal"}
                titel={`${INVOICE_STATUS[st].label} ${s.includes(st) ? "ausblenden" : "einblenden"}`}
                onClick={() => setzen({ status: statusParam(umschalten(s, st)) })}
              />
            ))}
            <Fuge />
            <Zaehlknopf
              label="Alle"
              sammel
              anzahl={gesamt}
              aktiv={alleAn}
              titel="Auch Entwürfe, Bezahltes und Storniertes"
              onClick={() => setzen({ status: "alle" })}
            />
          </Zaehlschiene>
        </>
      }
      unten={
        <>
          <Schiene>
            <Zaehlknopf
              label="Mahnung fällig"
              anzahl={mahnungFaellig}
              aktiv={ansicht.mahnung}
              ton="gefahr"
              titel="Überfällige Rechnungen, deren nächste Mahnstufe jetzt dran ist"
              onClick={() => setzen({ mahnung: ansicht.mahnung ? null : "faellig" })}
            />
          </Schiene>
          <Filterzurueck anzahl={aktiveFilter(ansicht)} params={ALLE_PARAMS} />
        </>
      }
    />
  );
}
